"use client";

import { useLayoutEffect, useRef, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import CatalogItemSnippet from "@/components/common/catalog-item-snippet";

interface CategoryCarouselMobileProps {
  title: string;
  seeAllHref: string | null;
  products: any[];
}

export default function CategoryCarouselMobile({
  title,
  seeAllHref,
  products,
}: CategoryCarouselMobileProps) {
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useLayoutEffect(() => {
    if (!scrollAreaRef.current) return;

    const viewport = scrollAreaRef.current.querySelector(
      "[data-radix-scroll-area-viewport]"
    ) as HTMLDivElement | null;

    if (!viewport) return;
    viewportRef.current = viewport;

    const update = () => {
      const el = viewportRef.current;
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanLeft(scrollLeft > 4);
      setCanRight(scrollLeft + clientWidth < scrollWidth - 4);
    };

    update();
    viewport.addEventListener("scroll", update);
    window.addEventListener("resize", update);

    return () => {
      viewport.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scroll = (dir: "left" | "right") => {
    const el = viewportRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold text-foreground tracking-tight -ml-5">{title}</h2>
        {!!seeAllHref && (
          <Link
            href={seeAllHref}
            className="ml-auto text-medium font-medium text-teal-500 hover:text-teal-600 cursor-pointer -mr-5"
          >
            Ver todos
          </Link>
        )}
      </div>

      {/* Scrollable items */}
      <div className="relative -mr-10 -ml-10 mt-1">
        {/* Arrows */}
        <button
          type="button"
          onClick={() => scroll("left")}
          className={`absolute left-1 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-background shadow transition-opacity duration-200 hover:bg-accent ${
            canLeft ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={() => scroll("right")}
          className={`absolute right-1 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-background shadow transition-opacity duration-200 hover:bg-accent ${
            canRight ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        <ScrollArea ref={scrollAreaRef} className="w-full whitespace-nowrap">
          <div className="flex w-max gap-3">
            <div className="w-[10px]"></div>
            {products.map((product) => (
              <div key={product.id} className="w-[40vw] shrink-0">
                <CatalogItemSnippet item={product} />
              </div>
            ))}
            <div className="w-[40vw] shrink-0 flex items-center justify-center">
              {!!seeAllHref && (
                <Link
                  href={seeAllHref}
                  className="flex flex-col items-center justify-center group"
                  tabIndex={-1}
                  onClick={e => e.stopPropagation()}
                >
                  <Button
                    size="icon"
                    className="pointer-events-auto h-14 w-14 rounded-full flex items-center justify-center hover:bg-teal-500 bg-teal-400 group-hover:bg-teal-500 cursor-pointer shadow-md transition group-hover:scale-105"
                    tabIndex={-1}
                    asChild
                  >
                    <span>
                      <ChevronRight className="h-12 w-12" />
                    </span>
                  </Button>
                  <span className="text-medium font-semibold text-teal-500 hover:text-teal-600 group-hover:text-teal-600 py-3 cursor-pointer transition-colors">
                    Ver todos
                  </span>
                </Link>
              )}
            </div>
          </div>
          <ScrollBar orientation="horizontal" className="hidden" />
        </ScrollArea>
      </div>
    </div>
  );
}
